/**
 * Lightweight client for Web Push notifications + badge counter.
 * Used by NotificationButton (opt-in) and AppLayout (badge polling).
 */
import api from "@/lib/api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export async function getPushPermission() {
  if (!pushSupported()) return "unsupported";
  return Notification.permission; // 'default' | 'granted' | 'denied'
}

export async function subscribeToPush() {
  if (!pushSupported()) throw new Error("Push non supportato su questo dispositivo");
  const perm = await Notification.requestPermission();
  if (perm !== "granted") {
    throw new Error("Permesso notifiche negato");
  }
  const reg = await navigator.serviceWorker.ready;
  // Get VAPID public key from env first (fast), fallback to backend
  let vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
  if (!vapidKey) {
    const { data } = await api.get("/notifications/vapid-public-key");
    vapidKey = data.publicKey;
  }
  if (!vapidKey) throw new Error("VAPID key mancante");

  // Re-use existing subscription when possible
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey),
    });
  }
  // Send subscription to backend (idempotent)
  const json = sub.toJSON();
  await api.post("/notifications/subscribe", {
    endpoint: json.endpoint,
    keys: json.keys,
  });
  return sub;
}

export async function unsubscribeFromPush() {
  if (!pushSupported()) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  try {
    await api.delete("/notifications/subscribe", { params: { endpoint: sub.endpoint } });
    } catch (error) {
      console.error("[push] subscription cleanup failed:", error);
    }
  await sub.unsubscribe();
}

export async function isSubscribed() {
  if (!pushSupported()) return false;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  return !!sub;
}
